import React, { useContext, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import AppUserDrawer from "pages/Services/AppUsers/app-user-drawer"
import SelectGroupFilters from "components/DropdownList/SelectGroupFilters"
import GlobalState from "contexts/GlobalState"
import TagsTree from "components/DropdownList/TagsTree"
import AppUsersSelected from "pages/Services/AppUsers/app-users-selected"
import {
  createApplicationUser,
  deleteApplicationUsers,
  getApplicationUsers,
  linkAppUsersToClient,
  unlinkAppUsersFromClient,
  getAppUsersLinks,
  moveAppUsersToGroup,
} from "helpers/backend_helper"
import {
  showToast,
  stringSorter,
  defaultPageSizeOptions,
  numberSorter,
  booleanSorter,
  dateSorterDateObj,
  dateFormatter,
  boolFormatterX,
  checkboxFormatter,
} from "utils/general"
import {
  Alert,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  FormGroup,
  DropdownMenu,
  DropdownItem,
  Label,
  Collapse,
} from "reactstrap"
import {
  Table as AntdTable,
  Popconfirm,
  Switch,
  Popover,
  Tooltip,
  Input as AntdInput,
  Form as AntdForm
} from "antd"
import { useFormik } from "formik"
import * as Yup from "yup"
import PubSub from "pubsub-js"
import XLSX from "xlsx"
import { tableDefaultHeight } from "constants/layout"

const AppUsers = props => {
  const [state] = useContext(GlobalState)
  const [userRole, setUserRole] = useState(null)
  const [group, setGroup] = useState()
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [addModal, setAddModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [ongoingAction, setOngoingAction] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedUsersArr, setSelectedUsersArr] = useState([])
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [selectedTags, setSelectedTags] = useState([])
  const [showOnSide, setShowOnSide] = useState(false)
  const [hideRelatives, setHideRelatives] = useState(false)
  const [colSize, setColSize] = useState(12)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [chosenLinkKey, setChosenLinkKey] = useState()
  const [linkCustomers, setLinkCustomers] = useState([])
  const [linkLoading, setLinkLoading] = useState(true)
  const [transferModal, setTransferModal] = useState(false)
  const [transferGroup, setTransferGroup] = useState()

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      setUserRole(role)
    }
    const token = PubSub.subscribe("page-refresh", (event, data) => {
      setGroup(props.group)
    })
    return () => {
      abortRef.current.abort()
      PubSub.unsubscribe(token)
    }
  }, [])

  useEffect(() => {
    if (props.group) setGroup(props.group)
  }, [props.group])

  useEffect(() => {
    // Skip if first load and has props.group, prevents loading full user list
    if (group == null && props.group) return

    getAppUsers()
    setFilteredUsers([])
  }, [group])

  useEffect(() => {
    const keys = selectedUsers.map(obj => obj.key)
    setSelectedUsersArr(keys)
  }, [selectedUsers])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, users, hideRelatives])

  // Reset form when modal changed
  useEffect(() => {
    addValidation.resetForm()
  }, [addModal])

  // Name tags state update from app-users-selected.js
  useEffect(() => {
    // console.log("Selected users updated", selectedUsers)
    if (selectedUsers.length == 0) {
      // Setting table checkboxes empty
      setSelectedRowKeys([])
    }
  }, [selectedUsers])

  // Showing selected users on own card
  // Reset form when modal changed
  useEffect(() => {
    if (showOnSide) {
      setColSize(9)
    } else {
      setColSize(12)
    }
  }, [showOnSide])

  // Get app users for table
  const getAppUsers = () => {
    setLoading(true)
    getApplicationUsers(abortRef.current, props.suvantoKey, group, true)
      .then(result => {
        //console.log("Entries: ", result)
        if (result.statusCode == 200 && result.entries) {
          result.entries.sort((a, b) => (a.name < b.name ? 1 : -1)) // Name sort
          result.entries.sort((a, b) => (a.relative > b.relative ? 1 : -1)) // Relatives at the end
          setUsers(result.entries)
        }
      })
      .catch(err => {
        console.log(err)
        setUsers([])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // Link selected users to client
  const linkUsers = () => {
    if (props.suvantoKey && !ongoingAction && selectedUsers.length > 0) {
      setOngoingAction(true)
      // console.log("selectedusers", selectedUsers)
      linkAppUsersToClient(abortRef.current, props.suvantoKey, selectedUsersArr)
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Success"), "success")
            setSelectedUsers([])
            getAppUsers()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingAction(false)
        })
    }
  }

  // Unlink selected users from client
  const unlinkUsers = () => {
    if (props.suvantoKey && !ongoingAction && selectedUsersArr.length > 0) {
      setOngoingAction(true)
      unlinkAppUsersFromClient(
        abortRef.current,
        props.suvantoKey,
        selectedUsersArr
      )
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Success"), "success")
            setSelectedUsers([])
            getAppUsers()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingAction(false)
        })
    }
  }

  // Delete selected users
  const deleteUsers = () => {
    if (!ongoingAction && selectedUsersArr.length > 0) {
      setOngoingAction(true)
      deleteApplicationUsers(abortRef.current, selectedUsersArr)
        .then(result => {
          if (result.statusCode == 200) {
            if (result.failed && result.failed.length > 0) {
              showToast(
                props.t(
                  "Unable to delete user, check and remove any existing links"
                ),
                "error",
                15000
              )
            } else {
              showToast(props.t("Deleted"), "success")
              setSelectedUsers([])
              getAppUsers()
            }
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingAction(false)
        })
    }
  }

  // Move users to a group
  const moveToGroup = () => {
    if (selectedRowKeys && transferGroup && !ongoingAction) {
      setOngoingAction(true)
      moveAppUsersToGroup(abortRef.current, transferGroup, selectedRowKeys)
        .then(result => {
          console.log("Move result: ", result)
          if (result.statusCode == 200) {
            setTransferModal(false)
            setSelectedRowKeys([])
            setSelectedUsers([])
            getAppUsers()
            showToast(props.t("Success"), "success")
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingAction(false)
        })
    }
  }

  // Create user validation
  const addValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      name: "",
      group: props.group,
      linkToCurrent: Boolean(props.usedIn["LinkAppUsers"]),
    },
    validationSchema: Yup.object({
      name: Yup.string().required(props.t("Required field")),
      username: Yup.string()
        .matches(
          /^[a-z0-9]+[a-z0-9-]*[a-z0-9]+[.][a-z0-9]+[a-z0-9-]*[a-z0-9]$/,
          props.t("Invalid format")
        )
        .trim()
        .min(7, props.t("Too short"))
        .required(props.t("Required field")),
    }),
    onSubmit: values => {
      console.log("Submit: ", values)
      if (!ongoingAction) {
        let clientKey = null
        if (values.linkToCurrent) clientKey = props.suvantoKey

        setOngoingAction(true)
        createApplicationUser(
          abortRef.current,
          values.username,
          values.group,
          values.name,
          clientKey
        )
          .then(result => {
            if (result.statusCode == 200) {
              showToast(props.t("User created"), "success")
              setAddModal(false)
              addValidation.resetForm()
              getAppUsers()
            } else if (result.statusMessage) {
              showToast(props.t(result.statusMessage), "error")
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            showToast(props.t("Error"), "error")
          })
          .finally(() => {
            setOngoingAction(false)
          })
      }
    },
  })

  // Handle search function
  const handleSearch = () => {
    let searchTextLowercase = searchText.toLowerCase()
    let result = users.filter(
      data =>
        (!hideRelatives || (hideRelatives && !data.relative)) &&
        (data.name?.toLowerCase().includes(searchTextLowercase) ||
          data.username?.toLowerCase().includes(searchTextLowercase) ||
          data.gsm?.toLowerCase().includes(searchTextLowercase) ||
          data.email?.toLowerCase().includes(searchTextLowercase))
    )
    setFilteredUsers(result)
  }

  // Popover links content
  const content = (
    <div>
      {linkCustomers.length > 0 && !linkLoading ? (
        <div>
          {linkCustomers.map((l, key) => (
            <p key={"" + key} className="mb-1">
              {l.clientName}
            </p>
          ))}
        </div>
      ) : (
        <div className="py-2 text-center">
          <p className="text-primary">
            <i className="bx bx-loader bx-spin font-size-18 align-middle" />
          </p>
        </div>
      )}
    </div>
  )

  // Table structure
  const columns = [
    {
      dataIndex: "edit",
      align: "center",
      title: props.t("Edit"),
      render: editFormatter,
      width: "100px",
      fixed: "left",
      ellipsis: true,
    },
    {
      dataIndex: "linked",
      title: props.t("Linked"),
      align: "center",
      render: checkboxFormatter,
      width: "100px",
      defaultSortOrder: "ascend",
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "linked", sortOrder)
      },
      hidden: props.usedIn["AppUserManagement"],
      ellipsis: true,
    },
    {
      dataIndex: "role",
      title: props.t("Role"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "role", sortOrder)
      },
      render: roleFormatter,
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "name",
      title: props.t("Name"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "username",
      title: props.t("Username"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "username", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "group", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "unit",
      title: props.t("Unit info"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "unit", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "gsm",
      title: props.t("GSM"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "gsm", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "email",
      title: props.t("Email"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "email", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "linkCount",
      title: props.t("Links"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["linkCount"], b["linkCount"], sortOrder)
      },
      render: linkFormatter,
      width: "200px",
      hidden: !group,
      ellipsis: true,
    },
    {
      dataIndex: "appVersion",
      title: props.t("App version"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "appVersion", sortOrder)
      },
      width: "200px",
      hidden: userRole !== "system_admin",
      ellipsis: true,
    },
    {
      dataIndex: "appOperatingSystem",
      title: props.t("Platform"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "appOperatingSystem", sortOrder)
      },
      width: "200px",
      hidden: userRole !== "system_admin",
      ellipsis: true,
    },
    {
      dataIndex: "lastConnected",
      title: props.t("Connected"),
      sorter: (a, b, sortOrder) => {
        return dateSorterDateObj(
          a["lastConnected"],
          b["lastConnected"],
          sortOrder
        )
      },
      render: dateFormatter,
      width: "200px",
      hidden: userRole !== "system_admin",
      ellipsis: true,
    },
    {
      dataIndex: "key",
      title: "GUID",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "key", sortOrder)
      },
      width: "200px",
      ellipsis: true,
      hidden: userRole !== "system_admin",
    },
  ]

  const onSelectChange = (newSelectedRowKeys, obj) => {
    setSelectedRowKeys(newSelectedRowKeys)

    let rowObject = obj.map(o => ({
      key: o.key,
      name: o.username,
      group: o.group,
    }))

    setSelectedUsers(rowObject)
  }

  // Select All Button operation
  const selectRow = {
    mode: "checkbox",
    selectedRowKeys,
    onChange: onSelectChange,
  }

  function roleFormatter(cell, row) {
    if (row.relative) {
      return <span className="text-primary">{props.t(row.role)}</span>
    }
    if (row.securityPerson) {
      return <span className="text-tertiary">{props.t(row.role)}</span>
    } else {
      return <span>{props.t(row.role)}</span>
    }
  }

  function editFormatter(cell, row) {
    return (
      <>
        <Button
          color="primary"
          outline
          size="sm"
          onClick={() => {
            setShowDrawer(true)
            setSelectedRow(row)
          }}
        >
          <i className="mdi mdi-pencil" />
        </Button>
      </>
    )
  }

  // Link formatter
  function settingLinkKey(key) {
    setChosenLinkKey(key)
    if (key != chosenLinkKey) {
      setLinkLoading(true)
      getAppUsersLinks(abortRef.current, key, [])
        .then(result => {
          if (result.statusCode == 200 && result.entries) {
            // console.log("fetch getAppUsersLinks results ", result)
            result.entries.sort((a, b) =>
              a.clientName > b.clientName ? 1 : -1
            )
            setLinkCustomers(result.entries)
          } else {
            showToast(props.t("Error"), "Error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "Error")
        })
        .finally(() => {
          setLinkLoading(false)
        })
    }
  }

  function linkFormatter(cell, row) {
    if (!cell) return <></>

    return (
      <Popover
        placement="right"
        content={content}
        title={props.t("Linked customers") + " - " + row.name}
        trigger="click"
      >
        <Button
          id={"btn_" + row.key}
          color="transparent"
          className="px-2 py-1 btn-hover-primary"
          onClick={() => settingLinkKey(row.key)}
        >
          {cell}
        </Button>

        {/* <UncontrolledTooltip placement="right" target={"btn_" + row.key}>
          {props.t("Show linked customers")}
        </UncontrolledTooltip> */}
      </Popover>
    )
  }

  // Group selection changed
  const groupChanged = newGroup => {
    if (newGroup) {
      setGroup(newGroup)
    } else {
      setGroup("")
    }
  }

  // Group selection changed
  const groupChanged2 = newGroup => {
    addValidation.setFieldValue("group", newGroup)
  }

  // Creating Excel workbook+worksheet+download
  const downloadExcel = () => {
    let title = props.t("Mobile app users")
    let message = props.t("Excel file exported succesfully!")
    const wb = XLSX.utils.book_new()

    const Heading = [
      [
        props.t("Linked"),
        props.t("Role"),
        props.t("Name"),
        props.t("Username"),
        props.t("Group"),
        props.t("GSM"),
        props.t("Email"),
        props.t("App version"),
        props.t("Platform"),
        props.t("Connected"),
        userRole == "system_admin" ? props.t("GUID") : null,
      ],
    ]
    const data = filteredUsers.map(elt => [
      boolFormatterX(elt.linked),
      elt.role,
      elt.name,
      elt.username,
      elt.group,
      elt.gsm,
      elt.email,
      elt.appVersion,
      elt.appOperatingSystem,
      elt.lastConnected,
      userRole == "system_admin" ? elt.key : null,
    ])

    // creating sheet and adding data from 2nd row of column A.
    // Leaving first row to add Heading
    const ws = XLSX.utils.json_to_sheet(data, {
      origin: "A2",
      skipHeader: true,
    })
    XLSX.utils.sheet_add_aoa(ws, Heading, { origin: "A1" })
    XLSX.utils.book_append_sheet(wb, ws, title)
    XLSX.write(wb, { bookType: "xlsx", type: "buffer" })
    XLSX.writeFile(wb, title + " (" + new Date() + ")" + ".xlsx")

    //Show toast
    showToast(message, "success")
  }

  return (
    <>
      <Row>
        {/* Side card of chosen users */}
        {showOnSide && (
          <Col sm="3">
            <Collapse isOpen={showOnSide}>
              <div className="wrapper">
                <Card>
                  <CardBody className="pt-3">
                    <AppUsersSelected
                      setSelectedUsers={setSelectedUsers}
                      selectedUsersObject={selectedUsers}
                      showOnSide={showOnSide}
                    ></AppUsersSelected>
                  </CardBody>
                </Card>
              </div>
            </Collapse>
          </Col>
        )}
        <Col sm={colSize}>
          <Card>
            <CardBody>
              {users != null && (
                <>
                  <Row>
                    <Col
                      className="mb-3"
                      style={{ minWidth: "200px", maxWidth: "400px" }}
                    >
                      <TagsTree
                        loadData={true}
                        expanded={true}
                        selectionChanged={setSelectedTags}
                      />
                    </Col>
                    <Col
                      className="mb-3"
                      style={{ minWidth: "200px", maxWidth: "400px" }}
                    >
                      <SelectGroupFilters
                        onChange={groupChanged}
                        multiple={false}
                        selection={group}
                        selectedTags={selectedTags}
                        disabled={loading}
                      />
                    </Col>
                    <Col className="mb-3" style={{ maxWidth: "250px" }}>
                      <AntdInput
                        placeholder={props.t("Search") + "..."}
                        allowClear
                        value={searchText}
                        onChange={event => {
                          setSearchText(event.target.value)
                        }}
                        style={{ minWidth: "160px" }}
                      />
                    </Col>
                    <Col className="mb-3 col-auto">
                      <Tooltip
                        title={props.t("Hide relatives")}
                        trigger={["hover", "focus"]}
                        placement="bottom"
                      >
                        <Switch
                          id="relativeSwitch"
                          className="mt-1"
                          checked={hideRelatives}
                          onChange={v => setHideRelatives(v)}
                          checkedChildren={
                            <div
                              className="d-flex"
                              style={{ alignItems: "center" }}
                            >
                              <i
                                className="bx bx-group font-size-16"
                                style={{ marginTop: "3px" }}
                              />
                            </div>
                          }
                          unCheckedChildren={
                            <div
                              className="d-flex"
                              style={{ alignItems: "center" }}
                            >
                              <i
                                className="bx bx-group font-size-16"
                                style={{ marginTop: "3px" }}
                              />
                            </div>
                          }
                        />
                      </Tooltip>
                      <Tooltip
                        title={props.t("Switch view of selected users")}
                        trigger={["hover", "focus"]}
                        placement="bottom"
                      >
                        <Switch
                          id="switchScene"
                          className="mt-1 ms-2"
                          checkedChildren={
                            <div
                              className="d-flex"
                              style={{ alignItems: "center" }}
                            >
                              <i
                                className="bx bx-window font-size-16"
                                style={{ marginTop: "3px" }}
                              />
                            </div>
                          }
                          unCheckedChildren={
                            <div
                              className="d-flex"
                              style={{ alignItems: "center" }}
                            >
                              <i
                                className="bx bx-list-minus font-size-16"
                                style={{ marginTop: "3px" }}
                              />
                            </div>
                          }
                          checked={showOnSide}
                          onChange={v => setShowOnSide(v)}
                        />
                      </Tooltip>
                    </Col>
                    <Col className="mb-3">
                      <div className="float-end d-inline-flex">
                        <Tooltip
                          title={props.t("Create a new app user")}
                          trigger={["hover", "focus"]}
                          placement="bottom"
                        >
                          <Button
                            className="compact-button"
                            color="success"
                            disabled={loading}
                            onClick={() => {
                              setAddModal(true)
                            }}
                          >
                            <i className="fas fa-user-plus"></i>
                            {!showOnSide && (
                              <span className="d-none d-xl-inline-block ms-2">
                                {props.t("Create a new user")}
                              </span>
                            )}
                          </Button>
                        </Tooltip>
                        <Dropdown
                          id="download-users-dropdown"
                          isOpen={menu}
                          toggle={() => setMenu(!menu)}
                          className="ms-3 download-btn text-center"
                          type="button"
                        >
                          <DropdownToggle
                            id="download-users"
                            className="text-muted"
                            tag="i"
                          >
                            <i className="mdi mdi-download" />
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => downloadExcel()}>
                              {props.t("Download .xlsx")}
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>

                  <Collapse isOpen={selectedUsers.length > 0}>
                    <Row className="pbb-3">
                      {!showOnSide && (
                        <AppUsersSelected
                          setSelectedUsers={setSelectedUsers}
                          selectedUsersObject={selectedUsers}
                          showOnSide={showOnSide}
                        />
                      )}
                      {props.usedIn["LinkAppUsers"] && (
                        <>
                          <Col className="col-auto">
                            <Button
                              color="success"
                              className="compact-button mb-3"
                              disabled={
                                selectedUsers.length == 0 ||
                                loading ||
                                ongoingAction
                              }
                              onClick={() => {
                                linkUsers()
                              }}
                            >
                              <i className="mdi mdi-link-variant-plus" />
                              <span className="d-none d-md-inline-block ms-2">
                                {props.t("Link selected users")}
                              </span>
                            </Button>
                            <Button
                              color="danger"
                              className="ms-3 mb-3 compact-button"
                              disabled={
                                selectedUsers.length == 0 ||
                                loading ||
                                ongoingAction
                              }
                              onClick={() => {
                                unlinkUsers()
                              }}
                            >
                              <i className="mdi mdi-link-variant-off" />
                              <span className="d-none d-md-inline-block ms-2">
                                {props.t("Unlink selected users")}
                              </span>
                            </Button>
                          </Col>
                          <Col></Col>
                        </>
                      )}
                      <Col className="col-auto">
                        {props.usedIn["AppUserManagement"] && (
                          <Button
                            color="primary"
                            className="me-3 mb-3 compact-button"
                            disabled={
                              selectedRowKeys.length == 0 ||
                              loading ||
                              ongoingAction
                            }
                            onClick={() => {
                              setTransferModal(true)
                            }}
                          >
                            <i className="mdi mdi-account-group" />
                            <span className="d-none d-lg-inline-block ms-2">
                              {props.t("Move to group") + "..."}
                            </span>
                          </Button>
                        )}
                        <Popconfirm
                          title={props.t("Confirm action")}
                          open={deleteConfirm}
                          okType="danger"
                          okText={props.t("Delete")}
                          cancelText={props.t("Cancel")}
                          onConfirm={() => {
                            deleteUsers()
                            setDeleteConfirm(false)
                          }}
                          onCancel={() => {
                            setDeleteConfirm(false)
                          }}
                        >
                          <Button
                            color="danger"
                            className="mb-3 compact-button"
                            disabled={
                              selectedUsers.length != 1 ||
                              loading ||
                              ongoingAction
                            }
                            onClick={() => {
                              setDeleteConfirm(true)
                            }}
                          >
                            <i className="mdi mdi-trash-can" />
                            <span className="d-none d-lg-inline-block ms-2">
                              {props.t("Delete selected user")}
                            </span>
                          </Button>
                        </Popconfirm>
                      </Col>
                    </Row>
                  </Collapse>

                  <div className="mt-1">
                    <AntdTable
                      className="colored-header narrow-header"
                      bordered
                      size="small"
                      loading={loading}
                      rowSelection={selectRow}
                      sticky
                      rowKey={"key"}
                      columns={columns}
                      dataSource={filteredUsers}
                      pagination={{
                        showTotal: total => `${total} ${props.t("users")}`,
                        showSizeChanger: true,
                        defaultPageSize: 100,
                        pageSizeOptions: defaultPageSizeOptions,
                      }}
                      tableLayout="auto"
                      scroll={{
                        x: "max-content",
                        y: tableDefaultHeight,
                      }}
                    />
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
        <Modal
          isOpen={addModal}
          toggle={() => {
            setAddModal(!addModal)
          }}
          centered
        >
          <ModalHeader>{props.t("Create a new app user")}</ModalHeader>
          <Form
            className="form-horizontal"
            onSubmit={e => {
              e.preventDefault()
              return false
            }}
          >
            <ModalBody>
              <Alert color={"primary"}>
                <span>{props.t("UserAllowedChars")}</span>
              </Alert>
              <FormGroup>
                <Label>{props.t("Username")}</Label>
                <AntdForm.Item
                  validateStatus={addValidation.touched.username && addValidation.errors.username ? "error" : ""}
                  hasFeedback
                  help={addValidation.touched.username && addValidation.errors.username ? addValidation.errors.username : ""}
                >
                  <AntdInput
                    name="username"
                    onBlur={addValidation.handleBlur}
                    allowClear
                    placeholder={props.t("E.g. firstname.lastname")}
                    value={addValidation.values.username}
                    onChange={(v) => { addValidation.setFieldValue("username", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
              <FormGroup>
                <Label>{props.t("Name")}</Label>
                <AntdForm.Item
                  validateStatus={addValidation.touched.name && addValidation.errors.name ? "error" : ""}
                  hasFeedback
                  help={addValidation.touched.name && addValidation.errors.name ? addValidation.errors.name : ""}
                >
                  <AntdInput
                    name="name"
                    onBlur={addValidation.handleBlur}
                    allowClear
                    placeholder={props.t("Firstname Lastname")}
                    value={addValidation.values.name}
                    onChange={(v) => { addValidation.setFieldValue("name", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
              <FormGroup>
                <Label>{props.t("Group")}</Label>
                <SelectGroupFilters
                  onChange={groupChanged2}
                  multiple={false}
                  clearable={false}
                  selection={addValidation.values.group}
                />
              </FormGroup>

              {props.usedIn["LinkAppUsers"] && (
                <FormGroup className="mt-4">
                  <Switch
                    className="me-2"
                    name="linkToCurrent"
                    checked={addValidation.values.linkToCurrent}
                    onChange={v => {
                      addValidation.setFieldValue("linkToCurrent", v)
                    }}
                  ></Switch>
                  <Label>{props.t("Link to current client")}</Label>
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                outline
                onClick={() => {
                  setAddModal(false)
                }}
              >
                {props.t("Cancel")}
              </Button>
              <Button
                color="success"
                disabled={
                  loading || ongoingAction || !addValidation.values.group
                }
                onClick={() => {
                  addValidation.submitForm()
                }}
              >
                {props.t("Add")}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal
          isOpen={transferModal}
          toggle={() => {
            setTransferModal(!transferModal)
          }}
          centered
        >
          <ModalHeader>{props.t("Move to group")}</ModalHeader>
          <Form
            className="form-horizontal"
            onSubmit={e => {
              e.preventDefault()
              return false
            }}
          >
            <ModalBody>
              <div className="mb-3">
                {selectedUsers.map((entry, index) => (
                  <span
                    key={"" + index}
                    className="me-1 mb-1 badge badge-soft-primary font-size-12"
                  >
                    {entry.name}
                  </span>
                ))}
              </div>
              <FormGroup>
                <SelectGroupFilters
                  onChange={setTransferGroup}
                  selection={transferGroup}
                  multiple={false}
                  clearable={true}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                outline
                onClick={() => {
                  setTransferModal(false)
                }}
              >
                {props.t("Cancel")}
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  moveToGroup()
                }}
                disabled={
                  selectedRowKeys.length == 0 ||
                  loading ||
                  ongoingAction ||
                  !transferGroup
                }
              >
                {props.t("Move")}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <AppUserDrawer
          showDrawer={showDrawer}
          setShowDrawer={setShowDrawer}
          selectedRow={selectedRow}
          getUsers={getAppUsers}
        />
      </Row>
    </>
  )
}

AppUsers.propTypes = {
  t: PropTypes.any,
  suvantoKey: PropTypes.string,
  group: PropTypes.string,
  usedIn: PropTypes.any,
}

export default withTranslation()(AppUsers)
